import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token — request a new link from the login page");
      return;
    }
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — you can now sign in");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
        <Card className="bg-[#121212] border-white/5 max-w-md w-full" data-testid="reset-success-card">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-white font-bold text-lg">Password updated</p>
            <p className="text-zinc-400 text-sm">Redirecting to sign-in…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
      <Card className="bg-[#121212] border-white/5 max-w-md w-full" data-testid="reset-password-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#0047FF]/10 flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-[#4D88FF]" />
          </div>
          <CardTitle className="text-2xl text-white">Set a new password</CardTitle>
          <CardDescription className="text-zinc-400">
            Choose something strong. You&apos;ll be signed in right after.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-pw" className="text-zinc-400">New password</Label>
              <Input
                id="new-pw"
                type="password"
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="reset-new-password"
              />
            </div>
            <div>
              <Label htmlFor="confirm-pw" className="text-zinc-400">Confirm password</Label>
              <Input
                id="confirm-pw"
                type="password"
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Type it again"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="reset-confirm-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl"
              data-testid="reset-submit"
            >
              {loading ? "Updating…" : "Update password"}
            </Button>
            {!token && (
              <p className="text-xs text-amber-300 text-center mt-2">
                Missing or invalid reset token. Request a new link from the login page.
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
